import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import axios from "axios"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { LayoutDashboard } from "lucide-react"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import withAuth from "../withAuth"
import { baseUrl } from "../../Network"
import { handleAuthError } from "../../utils"
import { useConfirm } from "../../contexts/ConfirmContext"
import { isMarketerReadOnly } from "../../helpers/adminRole"
import TestSettingsFields, { emptyTestSettings, settingsForSave } from "./TestSettingsFields"
import {
  SAMPLE_TEST_IMPORT,
  apiErrorMessage,
  downloadJson,
  exportShape,
  formatDateTime,
  testPayload,
  useTestFormOptions,
} from "./aiTestShared"

const ManageAiTests = () => {
  document.title = "Interactive tests | RYD Admin"
  const navigate = useNavigate()
  const { showConfirm } = useConfirm()
  const readOnly = isMarketerReadOnly()
  const fileRef = useRef(null)
  const [rows, setRows] = useState([])
  const { curricula, categories } = useTestFormOptions()
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(emptyTestSettings)
  const [saving, setSaving] = useState(false)
  /** `${action}:${id}` of the row action in progress, e.g. "publish:5", so only that button shows a spinner. */
  const [busy, setBusy] = useState(null)
  const [importing, setImporting] = useState(false)
  const isBusy = (action, id) => busy === `${action}:${id}`
  const rowBusy = (id) => !!busy && busy.endsWith(`:${id}`)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const res = await axios.get(`${baseUrl}/admin/ai-test/all`)
      if (res?.data?.status) setRows(Array.isArray(res.data.data) ? res.data.data : [])
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not load tests"))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openCreate = () => {
    setForm({ ...emptyTestSettings })
    setModal(true)
  }

  const create = async () => {
    if (!String(form.title).trim()) {
      toast.error("Give the test a title")
      return
    }
    try {
      setSaving(true)
      const res = await axios.post(`${baseUrl}/admin/ai-test`, testPayload(settingsForSave(form)))
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Could not create the test")
        return
      }
      toast.success("Test created. Add its questions next.")
      setModal(false)
      navigate(`/ai/tests/${res.data.data.id}`)
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not create the test"))
    } finally {
      setSaving(false)
    }
  }

  const togglePublished = async (r) => {
    if (busy) return
    try {
      setBusy(`publish:${r.id}`)
      const res = await axios.put(`${baseUrl}/admin/ai-test/publish/${r.id}`, { isPublished: !r.isPublished })
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Could not change publishing")
        return
      }
      toast.success(res.data.message)
      // Update just this row, so the table doesn't flash a full reload.
      const saved = res.data.data || {}
      setRows((list) =>
        list.map((row) =>
          row.id === r.id
            ? {
                ...row,
                isPublished: saved.isPublished ?? !r.isPublished,
                publishedAt: saved.publishedAt ?? row.publishedAt,
                updatedAt: saved.updatedAt ?? row.updatedAt,
              }
            : row
        )
      )
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not change publishing"))
    } finally {
      setBusy(null)
    }
  }

  const importTest = async (data, successMessage) => {
    const res = await axios.post(`${baseUrl}/admin/ai-test/import`, data)
    if (!res?.data?.status) throw new Error(res?.data?.message || "Import failed")
    toast.success(successMessage)
    const skipped = res.data.data?.skipped || []
    if (skipped.length) toast.warning(`Not found on this server, so left out: ${skipped.join("; ")}`, { autoClose: 10000 })
    await load()
    return res.data.data
  }

  const duplicate = async (r) => {
    if (busy) return
    try {
      setBusy(`duplicate:${r.id}`)
      const res = await axios.get(`${baseUrl}/admin/ai-test/${r.id}`)
      const data = exportShape(res.data.data)
      data.test.title = `${data.test.title} (copy)`.slice(0, 200)
      await importTest(data, "Test duplicated as a draft")
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not duplicate the test"))
    } finally {
      setBusy(null)
    }
  }

  const onImportFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    let data
    try {
      data = JSON.parse(await file.text())
    } catch {
      toast.error("That file is not valid JSON")
      return
    }
    if (!data || typeof data !== "object" || !data.test) {
      toast.error('The file must contain { "test": {...}, "questions": [...] }. Download the sample JSON to see the format.')
      return
    }
    try {
      setImporting(true)
      const created = await importTest(data, "Test imported as a draft")
      if (created?.id) navigate(`/ai/tests/${created.id}`)
    } catch (err) {
      handleAuthError(err)
      toast.error(apiErrorMessage(err, "Import failed"))
    } finally {
      setImporting(false)
    }
  }

  const remove = async (r) => {
    const ok = await showConfirm({
      title: "Delete test",
      message: `Delete “${r.title}”? Learners will no longer see it. Past results are kept.`,
      variant: "danger",
    })
    if (!ok) return
    try {
      setBusy(`delete:${r.id}`)
      await axios.delete(`${baseUrl}/admin/ai-test/${r.id}`)
      toast.success("Test deleted")
      setRows((list) => list.filter((row) => row.id !== r.id))
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not delete the test"))
    } finally {
      setBusy(null)
    }
  }

  const q = search.trim().toLowerCase()
  const visibleRows = q
    ? rows.filter((r) =>
        `${r.title} ${r.courseTitle || ""} ${r.level || ""} ${r.categoryTitle || ""} ${r.subcategoryTitle || ""}`
          .toLowerCase()
          .includes(q)
      )
    : rows

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="AI" breadcrumbItem="Interactive tests" />
          <div className="d-flex flex-wrap gap-2 justify-content-end align-items-center mb-3">
            <Button tag={Link} to="/ai/dashboard" color="light" size="sm">
              <LayoutDashboard size={16} className="me-1" style={{ verticalAlign: "text-top" }} />
              AI home
            </Button>
            <Button tag={Link} to="/ai/test-grades" color="light" size="sm">
              Test grading
            </Button>
            <Button tag={Link} to="/ai/test-attempts" color="light" size="sm">
              Attempts
            </Button>
            {!readOnly && (
              <>
                <Button
                  color="link"
                  size="sm"
                  className="px-1"
                  onClick={() => downloadJson(SAMPLE_TEST_IMPORT, "sample-test-import.json")}
                  title="Download an example file with one card of every type"
                >
                  Download sample JSON
                </Button>
                <Button color="secondary" outline size="sm" onClick={() => fileRef.current?.click()} disabled={importing}>
                  {importing ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Importing…
                    </>
                  ) : (
                    "Import JSON"
                  )}
                </Button>
                <input ref={fileRef} type="file" accept="application/json,.json" hidden onChange={onImportFile} />
                <Button color="primary" size="sm" onClick={openCreate}>
                  New test
                </Button>
              </>
            )}
          </div>
          <Card>
            <CardBody>
              <Row className="mb-3">
                <Col md={4}>
                  <Input
                    bsSize="sm"
                    placeholder="Search by title, course or level"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Col>
              </Row>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="align-middle table-hover mb-0">
                    <thead>
                      <tr>
                        <th title="Display order: lower numbers show first to learners">Order</th>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Course</th>
                        <th>Level</th>
                        <th>Questions</th>
                        <th>Attempts</th>
                        <th>Avg %</th>
                        <th>Published</th>
                        <th>Last modified</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map((r) => (
                        <tr key={r.id}>
                          <td className="small">{r.sortOrder ?? 0}</td>
                          <td>
                            <Link to={`/ai/tests/${r.id}`} className="fw-semibold">
                              {r.title}
                            </Link>
                            <div className="small text-muted">
                              {r.instantFeedback ? "Learning mode" : "Exam mode"}
                              {r.timeLimitMinutes ? ` · ${r.timeLimitMinutes} min` : ""}
                              {r.maxAttempts ? ` · ${r.maxAttempts} attempt(s)` : ""}
                            </div>
                          </td>
                          <td className="small">
                            {r.subcategoryTitle ? (
                              `${r.categoryTitle ? `${r.categoryTitle} → ` : ""}${r.subcategoryTitle}`
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="small">
                            {r.courseMissing ? (
                              <Badge color="warning">Course missing</Badge>
                            ) : (
                              r.courseTitle || <span className="text-muted">Standalone</span>
                            )}
                          </td>
                          <td className="small">{r.level || "—"}</td>
                          <td>{r.questionCount}</td>
                          <td>{r.attemptCount}</td>
                          <td>{r.avgPercent == null ? "—" : `${r.avgPercent}%`}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="form-check form-switch mb-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  checked={!!r.isPublished}
                                  disabled={readOnly || rowBusy(r.id)}
                                  onChange={() => togglePublished(r)}
                                  aria-label={r.isPublished ? "Unpublish" : "Publish"}
                                  style={{ cursor: readOnly ? "default" : "pointer" }}
                                />
                              </div>
                              {isBusy("publish", r.id) && <Spinner size="sm" color="primary" />}
                            </div>
                          </td>
                          <td className="small" title={r.updatedAt || ""}>
                            {formatDateTime(r.updatedAt)}
                          </td>
                          <td className="text-end text-nowrap">
                            <Button size="sm" color="secondary" outline className="me-1" tag={Link} to={`/ai/tests/${r.id}`}>
                              {readOnly ? "View" : "Edit"}
                            </Button>
                            {!readOnly && (
                              <>
                                <Button
                                  size="sm"
                                  color="info"
                                  outline
                                  className="me-1"
                                  disabled={rowBusy(r.id)}
                                  onClick={() => duplicate(r)}
                                >
                                  {isBusy("duplicate", r.id) ? <Spinner size="sm" /> : "Duplicate"}
                                </Button>
                                <Button size="sm" color="danger" outline disabled={rowBusy(r.id)} onClick={() => remove(r)}>
                                  {isBusy("delete", r.id) ? <Spinner size="sm" /> : "Delete"}
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!visibleRows.length && (
                        <tr>
                          <td colSpan={11} className="text-center text-muted py-4">
                            {rows.length ? "No tests match your search." : "No tests yet. Create one with “New test”."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {!readOnly && (
        <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
          <ModalHeader toggle={() => setModal(false)}>New test</ModalHeader>
          <ModalBody>
            <TestSettingsFields form={form} setForm={setForm} curricula={curricula} categories={categories} tests={rows} />
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setModal(false)}>
              Cancel
            </Button>
            <Button color="primary" onClick={create} disabled={saving}>
              {saving ? <Spinner size="sm" /> : "Create and add questions"}
            </Button>
          </ModalFooter>
        </Modal>
      )}
      <ToastContainer />
    </React.Fragment>
  )
}

export default withAuth(ManageAiTests)
